import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import authApi from '@/api/auth';
import { onboardingApi } from '@/api/onboarding';
import { setupApi } from '@/api/setup';
import { useTranslation } from '@/hooks/useTranslation';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { PERMISSIONS } from '@/lib/permissions';

interface AuthInitializerProps {
  children: React.ReactNode;
}

/**
 * Public auth routes that must render independently of the /api/admin/me probe.
 * These pages are shown to unauthenticated users and should NEVER be gated
 * behind a "Verifying authentication..." spinner.
 */
const PUBLIC_AUTH_PATHS = ['/login', '/forgot-password', '/admin/reset-password'];

/** Maximum ms to wait for the /api/admin/me probe before treating as unauthenticated. */
const AUTH_PROBE_TIMEOUT_MS = 8000;

/**
 * AuthInitializer component
 *
 * Checks authentication state on app mount by calling /api/admin/me.
 * This ensures the auth store is populated with the current user if they have
 * a valid session, preventing unnecessary redirects to login on page refresh.
 *
 * Fail-open: if the probe errors (401/500/network) OR exceeds AUTH_PROBE_TIMEOUT_MS,
 * treats the user as unauthenticated and lets the router decide (→ /login).
 * Public auth pages (/login, /forgot-password, /reset-password) always render
 * immediately without waiting for the probe — they must work even when the
 * backend is completely unreachable.
 */
export function AuthInitializer({ children }: AuthInitializerProps) {
  const { t } = useTranslation('auth');
  const { setUser, setLoading, setInitialized, setOnboardingRequired, setSetupRequired, setTwoFactorRequirement } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);
  // Tracks whether the timeout fired first so we don't double-resolve.
  const resolvedRef = useRef(false);

  useEffect(() => {
    const finalize = (user: Parameters<typeof setUser>[0] = null) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      setUser(user);
      if (!user) {
        setSetupRequired(false);
        setOnboardingRequired(false);
      }
      setInitialized(true);
      setLoading(false);
      setIsChecking(false);
    };

    const checkAuthentication = async () => {
      // Bundle K Phase K1: skip admin auth probe on portal pages to avoid
      // unnecessary `/api/admin/me` 401 noise while a customer is signed in.
      const currentPath =
        typeof window !== 'undefined' ? window.location.pathname : '/';
      const isPortalPath = currentPath.startsWith('/portal/');
      if (isPortalPath) {
        finalize(null);
        return;
      }

      setLoading(true);

      // Race the auth probe against a hard timeout — if the backend hangs
      // (e.g. returns a non-JSON 500 HTML page that the client has to fully
      // stream) we must not leave the user on an infinite spinner.
      const timeoutId = setTimeout(() => {
        console.warn('[AuthInitializer] auth probe timed out after', AUTH_PROBE_TIMEOUT_MS, 'ms — treating as unauthenticated');
        finalize(null);
      }, AUTH_PROBE_TIMEOUT_MS);

      try {
        const session = await authApi.checkAuth();
        clearTimeout(timeoutId);

        if (!session?.user) {
          finalize(null);
          return;
        }

        const { user } = session;
        setTwoFactorRequirement(session.requiresTwoFactor, session.requiresTwoFactorEnrollment);

        // W1.2 — a session that still needs a 2FA challenge or mandatory
        // enrollment can't reach setup/onboarding endpoints either (they're
        // gated too) — skip straight to finalize and let ProtectedRoute
        // route to /2fa/challenge or /2fa/enroll.
        if (session.requiresTwoFactor || session.requiresTwoFactorEnrollment) {
          setSetupRequired(false);
          setOnboardingRequired(false);
          finalize(user);
          return;
        }

        // User is authenticated — populate the store, then check wizard state.
        // We do this BEFORE calling finalize so isAuthenticated is set along
        // with the onboarding/setup flags in one pass.
        const hasSettingsPermission =
          user.permissions?.includes(PERMISSIONS.SETTINGS_UPDATE) ||
          user.roles?.includes('super_admin');

        if (hasSettingsPermission) {
          try {
            const setupStatus = await setupApi.getStatus();
            const setupDone = setupStatus.setup_complete;
            setSetupRequired(!setupDone);

            if (setupDone) {
              const onboardingStatus = await onboardingApi.getStatus();
              setOnboardingRequired(!onboardingStatus.completed);
            } else {
              setOnboardingRequired(false);
            }
          } catch {
            // Wizard status failures are non-fatal — don't block the app.
            setSetupRequired(false);
            setOnboardingRequired(false);
          }
        } else {
          setSetupRequired(false);
          setOnboardingRequired(false);
        }

        finalize(user);
      } catch (error) {
        clearTimeout(timeoutId);
        // Error checking auth (likely 401 or network error) — unauthenticated.
        console.error('[AuthInitializer] auth check failed:', error);
        finalize(null);
      }
    };

    checkAuthentication();
  }, [setUser, setLoading, setInitialized, setOnboardingRequired, setSetupRequired, setTwoFactorRequirement]);

  // Public auth pages (/login, /forgot-password, /reset-password) MUST render
  // immediately — they are the escape hatch for unauthenticated users and
  // should never be blocked by the probe spinner.
  const currentPath =
    typeof window !== 'undefined' ? window.location.pathname : '/';
  const isPublicAuthPath = PUBLIC_AUTH_PATHS.some((p) => currentPath.startsWith(p));
  if (isPublicAuthPath) {
    return <>{children}</>;
  }

  // Show loading spinner while checking authentication on non-admin paths
  // (e.g. the root "/" redirect). Admin-shell paths render straight through —
  // ProtectedRoute shows the AdminLayout chrome with a content-area spinner.
  if (isChecking) {
    const isPortalPath = currentPath.startsWith('/portal/');
    const isAdminShellPath =
      currentPath.startsWith('/admin') || currentPath.startsWith('/dashboard');

    // Optimistic render: if a session hint is present the operator very likely
    // has a valid Sanctum cookie (set on last successful login/reload). Skip
    // the blocking full-screen spinner and render children immediately.
    // ProtectedRoute remains the strict authority — if the probe resolves as
    // unauthenticated it clears the hint via setUser(null) and ProtectedRoute
    // redirects to /login, self-correcting within the same page load.
    // Portal paths are intentionally excluded: they have their own auth flow
    // and must not receive admin children during the probe window.
    if (!isPortalPath) {
      const hasSessionHint =
        typeof window !== 'undefined' &&
        localStorage.getItem('admin_session_hint') === '1';
      if (hasSessionHint) {
        return <>{children}</>;
      }
    }

    if (!isAdminShellPath) {
      return <LoadingSpinner text={t('verifying_authentication')} size="lg" />;
    }
  }

  // Authentication check complete (or deferred to ProtectedRoute), render the app.
  return <>{children}</>;
}
